import {
  AsYouType,
  formatIncompletePhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

/**
 * Phone handling is built on libphonenumber-js (Google's metadata) rather than
 * a hand-rolled trunk-prefix table: the picker offers ~240 countries, and the
 * rules genuinely differ — a leading 0 is a national trunk prefix in ZA/AU/GB
 * that must be dropped for E.164, but in Italy it is part of the number and
 * must be kept. Getting that wrong silently corrupts a customer's identity.
 */

/**
 * Whether to store and authenticate with canonical E.164.
 *
 * OFF until `apps/functions/scripts/auditPhoneNumbers.ts --apply` has run
 * against production. Identity Platform does not canonicalise phone numbers —
 * verified against the auth emulator, `+610481195526` and `+61481195526`
 * resolve to two different accounts — so flipping this before the existing
 * numbers are migrated would sign returning customers into brand-new accounts
 * and cut them off from their loyalty profile.
 *
 * Run the audit (read-only) to see the affected count; if it reports zero
 * non-canonical numbers, this can be turned on straight away.
 */
export const NORMALISE_TO_E164 = false;

export interface PhoneDraft {
  /** Canonical E.164 to store/submit — "" until the number could be real. */
  e164: string;
  /** Country the digits resolve to; follows a pasted "+<cc>" prefix. */
  country: CountryCode | undefined;
  /** Whether the digits are a plausible length for that country. */
  possible: boolean;
}

const asCountry = (code: string | undefined): CountryCode | undefined =>
  code ? (code.toUpperCase() as CountryCode) : undefined;

/**
 * The digits of a number as it is dialled domestically — including any national
 * trunk prefix. This is what a person types and what we render in the field, so
 * `draftPhone` round-trips it straight back to the same E.164.
 */
const nationalDigits = (
  parsed: NonNullable<ReturnType<typeof parsePhoneNumberFromString>>,
): string =>
  parsed.country
    ? parsed.formatNational().replace(/\D/g, "")
    : parsed.nationalNumber;

/**
 * Interprets what the user has typed as a national number for `country`.
 * Emits E.164 only once the number is a plausible length, so a half-typed
 * number never reaches Firebase and callers can gate their submit on it.
 */
export const draftPhone = (
  input: string,
  country: string | undefined,
): PhoneDraft => {
  const formatter = new AsYouType(asCountry(country));
  formatter.input(input);
  const possible = formatter.isPossible();
  return {
    e164: possible ? (formatter.getNumberValue() ?? "") : "",
    country: formatter.getCountry() ?? asCountry(country),
    possible,
  };
};

/**
 * The value to authenticate with and store: canonical E.164 once
 * `NORMALISE_TO_E164` is on, otherwise the legacy "dial code + digits as
 * typed" shape that the accounts already in Firebase Auth are keyed on.
 * Either way, "" until the number could be real, so callers can gate submit.
 */
export const phoneToSubmit = (
  draft: PhoneDraft,
  legacyForm: string,
  normalise: boolean = NORMALISE_TO_E164,
): string => (draft.possible ? (normalise ? draft.e164 : legacyForm) : "");

/** National-format text for display, e.g. "0821234567" → "082 123 4567". */
export const formatNational = (
  input: string,
  country: string | undefined,
): string => formatIncompletePhoneNumber(input, asCountry(country));

/**
 * Splits a stored E.164 number into the parts a picker + national field need.
 * Returns null for anything that isn't a parseable international number —
 * legacy locally-formatted values must not be re-displayed under a guessed
 * country. `country` is undefined for reserved/fictional ranges that no single
 * country claims, so callers fall back to `callingCode`.
 */
export const splitE164 = (
  phone: string | null | undefined,
): { country?: CountryCode; callingCode: string; national: string } | null => {
  const trimmed = phone?.trim();
  if (!trimmed?.startsWith("+")) return null;
  const parsed = parsePhoneNumberFromString(trimmed);
  if (!parsed) return null;
  return {
    country: parsed.country,
    callingCode: parsed.countryCallingCode,
    national: nationalDigits(parsed),
  };
};
